import React from 'react';
import { Link } from 'react-router-dom';

/** English body for /privacy (aligned with German version, 23 April 2026). */
export const PrivacyContentEn: React.FC = () => (
  <>
    <p>
      <strong>Privacy Policy for I Think It’s You</strong>
    </p>
    <p>
      <em>Version: 23 April 2026</em>
    </p>

    <h2>1. Who we are</h2>
    <p>
      This privacy policy describes how we process personal data when you use the website, mobile
      applications, and other digital services of <strong>I Think It’s You</strong>, operating as{' '}
      <em>I Think It’s You</em> and <em>Event Partner Austria Verein</em> (“we”, “us”, “our”).
    </p>
    <p>
      <strong>Controller:</strong>
      <br />
      Event Partner Austria Verein
      <br />
      Kwizdastrasse 9/11
      <br />
      2100 Korneuburg
      <br />
      Austria
    </p>
    <p>
      Email:{' '}
      <a href="mailto:contact@i-think-its-you.at">contact@i-think-its-you.at</a>
      <br />
      Contact form:{' '}
      <Link to="/contact">Contact form</Link>
    </p>
    <p>
      <strong>Data Protection Officer:</strong>
      <br />
      Michael Kremnitzer
      <br />
      <a href="mailto:michael.kremnitzer@eventpartner.at">michael.kremnitzer@eventpartner.at</a>
    </p>

    <h2>2. Who this privacy policy applies to</h2>
    <p>This privacy policy applies to:</p>
    <ul>
      <li>Visitors to our website and app</li>
      <li>registered users</li>
      <li>people who contact us</li>
      <li>
        people whose personal data is contained in reports, abuse notifications, or support requests
      </li>
      <li>and people who may be described or mentioned in public posts</li>
    </ul>
    <p>
      Because I Think It’s You is a service for “missed connections”, we may in some circumstances
      also process data relating to non-registered third parties.
    </p>

    <h2>3. What data we process</h2>

    <h3>3.1 Data you provide to us directly</h3>
    <ul>
      <li>Account data (username, email address, password hash, time of registration)</li>
      <li>Profile data (display name, profile picture, language, city/region)</li>
      <li>Content data (posts, messages, comments, etc.)</li>
      <li>Communication data (support requests, complaints, etc.)</li>
      <li>Payment and transaction data (metadata)</li>
      <li>Data from social login features</li>
    </ul>

    <h3>3.2 Data relating to other people</h3>
    <ul>
      <li>Description of an encounter</li>
      <li>Location and time</li>
      <li>Outwardly recognisable characteristics</li>
      <li>Communication history</li>
      <li>Information contained in reports or complaints</li>
    </ul>
    <blockquote className="static-page-quote">
      <p>
        <strong>Note:</strong> The service is <strong>not intended</strong> to make identifying or
        sensitive data about third parties public.
      </p>
    </blockquote>

    <h3>3.3 Data collected automatically</h3>
    <ul>
      <li>IP address</li>
      <li>Timestamps</li>
      <li>Browser type and version</li>
      <li>Device type, operating system</li>
      <li>Referrer URL</li>
      <li>Log data</li>
      <li>Cookie, consent, and session data</li>
      <li>Approximate location data (IP-based)</li>
      <li>More precise location data (only with consent)</li>
    </ul>

    <h3>3.4 Data from third parties</h3>
    <ul>
      <li>Social login providers</li>
      <li>Payment service providers</li>
      <li>Other users (reports)</li>
      <li>Technical service providers</li>
      <li>Authorities or courts</li>
    </ul>

    <h2>4. Purposes and legal bases for processing</h2>
    <p>We process data only on the basis of:</p>
    <ul>
      <li>consent</li>
      <li>performance of a contract</li>
      <li>legal obligation</li>
      <li>legitimate interests</li>
    </ul>

    <h3>4.1 Providing the service</h3>
    <ul>
      <li>Create and manage accounts</li>
      <li>Operate the platform</li>
      <li>Publish posts</li>
      <li>Enable communication</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> performance of a contract
    </p>

    <h3>4.2 Operating the “missed connections” service</h3>
    <ul>
      <li>Display public posts</li>
      <li>Provide search features</li>
      <li>Enable people to initiate contact</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> legitimate interests
    </p>

    <h3>4.3 Moderation &amp; security</h3>
    <ul>
      <li>Detect abuse</li>
      <li>Review and remove content</li>
      <li>Protect systems</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> legitimate interests
    </p>

    <h3>4.4 Communication &amp; support</h3>
    <ul>
      <li>Respond to enquiries</li>
      <li>Handle complaints</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> contract, obligation, or legitimate interest
    </p>

    <h3>4.5 Location features</h3>
    <ul>
      <li>Regional assignment</li>
      <li>Local search</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> contract / consent
    </p>

    <h3>4.6 Marketing &amp; newsletter</h3>
    <p>Only with consent or another lawful basis.</p>
    <p>You may withdraw consent at any time.</p>

    <h3>4.7 Analytics &amp; further development</h3>
    <ul>
      <li>Understand usage</li>
      <li>Analyse errors</li>
      <li>Improve features</li>
    </ul>
    <p>
      <strong>Legal basis:</strong> legitimate interests / consent
    </p>

    <h3>4.8 Legal obligations</h3>
    <ul>
      <li>Comply with statutory requirements</li>
      <li>Assert claims</li>
    </ul>

    <h2>5. Special categories of personal data</h2>
    <p>Please do not submit <strong>sensitive data</strong> such as:</p>
    <ul>
      <li>Health data</li>
      <li>Religious beliefs</li>
      <li>Biometric data</li>
      <li>Sexual orientation</li>
    </ul>
    <p>
      If such data is published voluntarily, we will process it only within the limits permitted by
      law.
    </p>

    <h2>6. Public areas</h2>
    <ul>
      <li>Posts are publicly visible</li>
      <li>Content may be shared or indexed</li>
      <li>Third-party rights must be respected</li>
    </ul>
    <p>
      <strong>Recommendation:</strong> Do not publish identifying or sensitive data.
    </p>

    <h2 id="cookies">7. Cookies and similar technologies</h2>
    <p>We use cookies for:</p>
    <ul>
      <li>Operating the platform</li>
      <li>Security</li>
      <li>Analytics (only with consent)</li>
    </ul>
    <p>You may withdraw consent at any time.</p>
    <p>
      Links:
      <br />
      <a href="#cookies">Cookie notice</a>
      <br />
      <a href="#cookies">Cookie settings</a>
    </p>

    <h2>8. Recipients of data</h2>
    <p>Possible recipients include:</p>
    <ul>
      <li>Hosting and cloud providers</li>
      <li>Payment service providers</li>
      <li>Analytics providers</li>
      <li>Support service providers</li>
      <li>Authorities</li>
    </ul>

    <h2>9. Transfers to third countries</h2>
    <p>Data is transferred only with appropriate safeguards, e.g.:</p>
    <ul>
      <li>EU adequacy decision</li>
      <li>EU-US Data Privacy Framework</li>
      <li>Standard contractual clauses</li>
    </ul>

    <h2>10. Retention</h2>
    <p>Data is stored:</p>
    <ul>
      <li>only as long as necessary</li>
      <li>in accordance with statutory periods</li>
    </ul>
    <p>Examples:</p>
    <ul>
      <li>Account data: for the lifetime of the account</li>
      <li>Posts: until deletion</li>
      <li>Logs: limited period</li>
      <li>Accounting: statutory retention periods</li>
    </ul>

    <h2>11. Your rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li>Access</li>
      <li>Rectification</li>
      <li>Erasure</li>
      <li>Restriction of processing</li>
      <li>Data portability</li>
      <li>Object</li>
      <li>Withdraw consent</li>
    </ul>
    <p>
      <strong>Contact:</strong>{' '}
      <a href="mailto:contact@i-think-its-you.at">contact@i-think-its-you.at</a>
    </p>

    <h2>12. Right to lodge a complaint</h2>
    <p>
      Austrian Data Protection Authority
      <br />
      Barichgasse 40–42
      <br />
      1030 Vienna
      <br />
      Austria
    </p>
    <p>
      Email: <a href="mailto:dsb@dsb.gv.at">dsb@dsb.gv.at</a>
    </p>

    <h2>13. Obligation to provide data</h2>
    <p>Certain data is required (e.g. login credentials).</p>
    <p>Without it, the service is limited.</p>

    <h2>14. Automated decision-making</h2>
    <p>We use systems for:</p>
    <ul>
      <li>Spam detection</li>
      <li>Ranking or sorting content</li>
      <li>Security</li>
    </ul>
    <p>
      No solely automated decisions with legal or similarly significant effect (unless separately
      stated).
    </p>

    <h2>15. Data security</h2>
    <p>Measures include:</p>
    <ul>
      <li>Encryption</li>
      <li>Access restrictions</li>
      <li>Backups</li>
      <li>Security processes</li>
    </ul>
    <blockquote className="static-page-quote">
      <p>Absolute security cannot be guaranteed.</p>
    </blockquote>

    <h2>16. Minors</h2>
    <p>
      The service is <strong>not directed at people under 18</strong>.
      <br />
      If we become aware of such data, we will delete it.
    </p>

    <h2>17. Changes</h2>
    <p>This privacy policy may be updated.</p>
    <p>The current version is available on the website/app.</p>

    <h2>18. Contact</h2>
    <p>
      Event Partner Austria Verein
      <br />
      Kwizdastrasse 9/11
      <br />
      2100 Korneuburg
      <br />
      Austria
    </p>
    <p>
      Email: <a href="mailto:contact@i-think-its-you.at">contact@i-think-its-you.at</a>
    </p>
  </>
);
